import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';
import {
  Key,
  Mail,
  Shield,
  LogIn,
  Copy,
  Eye,
  EyeOff,
  Clock,
  AlertTriangle,
  CheckCircle,
  Loader2,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../hooks/use-toast';

const VendorCredentialsTab = ({ vendorId, vendorEmail, vendorName }) => {
  const { apiCall, isSuperAdmin } = useAuth();
  const { toast } = useToast();

  // State
  const [credentialsInfo, setCredentialsInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [impersonating, setImpersonating] = useState(false);

  // Dialog states
  const [tempPasswordDialog, setTempPasswordDialog] = useState({ open: false, password: null });
  const [emailDialog, setEmailDialog] = useState({ open: false });
  const [impersonateDialog, setImpersonateDialog] = useState({ open: false });
  const [showPassword, setShowPassword] = useState(false);

  // Form data
  const [emailForm, setEmailForm] = useState({
    include_temp_password: true,
    custom_message: '',
  });
  const [impersonateReason, setImpersonateReason] = useState('');

  useEffect(() => {
    fetchCredentialsInfo();
  }, [vendorId]);

  const fetchCredentialsInfo = async () => {
    try {
      setLoading(true);
      const data = await apiCall(`/api/vendors/${vendorId}/credentials`);
      setCredentialsInfo(data);
    } catch (error) {
      console.error('Failed to fetch credentials info:', error);
      toast({
        title: 'Error',
        description: 'Failed to load credentials information',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateTempPassword = async () => {
    try {
      setGenerating(true);
      const response = await apiCall(`/api/vendors/${vendorId}/generate-temp-password`, {
        method: 'POST',
        body: JSON.stringify({
          vendor_id: vendorId,
          send_email: false,
        }),
      });

      setTempPasswordDialog({ open: true, password: response.temp_password });
      await fetchCredentialsInfo();

      toast({
        title: 'Password Generated',
        description: 'Temporary password created successfully',
      });
    } catch (error) {
      console.error('Failed to generate temp password:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to generate temporary password',
        variant: 'destructive',
      });
    } finally {
      setGenerating(false);
    }
  };

  const handleSendCredentialsEmail = async () => {
    try {
      setSendingEmail(true);
      await apiCall(`/api/vendors/${vendorId}/send-credentials-email`, {
        method: 'POST',
        body: JSON.stringify({
          vendor_id: vendorId,
          include_temp_password: emailForm.include_temp_password,
          custom_message: emailForm.custom_message || null,
        }),
      });

      setEmailDialog({ open: false });
      setEmailForm({ include_temp_password: true, custom_message: '' });
      await fetchCredentialsInfo();

      toast({
        title: 'Email Sent',
        description: `Credentials email sent to ${vendorEmail}`,
      });
    } catch (error) {
      console.error('Failed to send email:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to send credentials email',
        variant: 'destructive',
      });
    } finally {
      setSendingEmail(false);
    }
  };

  const handleImpersonate = async () => {
    try {
      setImpersonating(true);
      const response = await apiCall(`/api/vendors/${vendorId}/impersonate`, {
        method: 'POST',
        body: JSON.stringify({
          vendor_id: vendorId,
          reason: impersonateReason || null,
        }),
      });

      // iter-232.J (RC-1) — Save admin originals to BOTH sessionStorage
      // AND localStorage. The previous version only wrote to
      // sessionStorage, which left localStorage with the admin token
      // active — meaning any new tab triggered `rehydrateSessionFromLocal`
      // and ended up running as admin (since sessionStorage was empty
      // in the new tab but localStorage had the admin token). On the
      // first 401 of any vendor-only call, the refresh-token endpoint
      // then minted a fresh admin access token and persisted it across
      // every store, silently destroying the impersonation session.
      const adminToken = sessionStorage.getItem('token')
        || localStorage.getItem('token') || '';
      const adminUser = sessionStorage.getItem('user')
        || localStorage.getItem('user') || '';
      const adminRefreshToken = sessionStorage.getItem('refresh_token')
        || localStorage.getItem('refresh_token') || '';
      const writeBoth = (k, v) => {
        try { sessionStorage.setItem(k, v); } catch (_e) { /* no-op */ }
        try { localStorage.setItem(k, v); } catch (_e) { /* no-op */ }
      };
      writeBoth('admin_original_token', adminToken);
      writeBoth('admin_original_user', adminUser);
      if (adminRefreshToken) writeBoth('admin_original_refresh_token', adminRefreshToken);
      writeBoth('impersonation_session_id', response.session_id);
      writeBoth('impersonating_vendor', JSON.stringify({
        vendor_id: response.vendor_id,
        vendor_email: response.vendor_email,
        vendor_name: response.vendor_name,
        expires_in: response.expires_in,
      }));

      // Switch to vendor token AND update user object across BOTH stores
      // so a new tab / cross-tab rehydrate picks up the impersonation
      // context rather than the stale admin token.
      writeBoth('token', response.access_token);

      // Create vendor user object for AuthContext.
      const vendorUser = {
        id: response.vendor_id,
        email: response.vendor_email,
        name: response.vendor_name,
        role: 'vendor',
        is_impersonation: true,
        impersonation_session_id: response.session_id,
        lastLogin: new Date().toISOString()
      };
      writeBoth('user', JSON.stringify(vendorUser));

      // iter-232.J — The admin's refresh_token in cookie/body would
      // otherwise be used by tryRefreshToken to swap back to an admin
      // access token on the first 401. We explicitly REMOVE the
      // admin's stored refresh_token from sessionStorage/localStorage
      // so the body-fallback path can't fire. (The HttpOnly cookie
      // stays until end-of-impersonation; the backend defense-in-depth
      // in /api/security/refresh-token also blocks this path.)
      try { sessionStorage.removeItem('refresh_token'); } catch (_e) { /* no-op */ }
      try { localStorage.removeItem('refresh_token'); } catch (_e) { /* no-op */ }

      toast({
        title: 'Impersonation Started',
        description: `You are now logged in as ${response.vendor_name}. Session expires in 1 hour.`,
      });

      // Redirect to vendor dashboard - use full reload to reset AuthContext
      window.location.href = '/dashboard';
    } catch (error) {
      console.error('Failed to impersonate:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to start impersonation',
        variant: 'destructive',
      });
    } finally {
      setImpersonating(false);
      setImpersonateDialog({ open: false });
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast({
      title: 'Copied',
      description: 'Password copied to clipboard',
    });
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="py-10 text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-muted-foreground" />
          <p className="mt-2 text-muted-foreground">Loading credentials information...</p>
        </CardContent>
      </Card>
    );
  }

  // Check if user has super admin access
  if (!isSuperAdmin) {
    return (
      <Card>
        <CardContent className="py-10 text-center">
          <Shield className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <h3 className="font-semibold text-lg">Access Restricted</h3>
          <p className="text-muted-foreground">
            Only Super Admins can access vendor credentials management.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Credentials Info Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Credential Status
          </CardTitle>
          <CardDescription>
            View and manage vendor login credentials. Passwords are never visible.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-muted-foreground">Email</Label>
              <p className="font-medium">{credentialsInfo?.vendor_email}</p>
            </div>
            <div className="space-y-2">
              <Label className="text-muted-foreground">Account Status</Label>
              <Badge
                variant={credentialsInfo?.account_status === 'active' ? 'default' : 'secondary'}
              >
                {credentialsInfo?.account_status}
              </Badge>
            </div>
            <div className="space-y-2">
              <Label className="text-muted-foreground">Last Login</Label>
              <p className="font-medium">
                {credentialsInfo?.last_login_at
                  ? new Date(credentialsInfo.last_login_at).toLocaleString()
                  : 'Never'}
              </p>
            </div>
            <div className="space-y-2">
              <Label className="text-muted-foreground">Password Changed</Label>
              <p className="font-medium">
                {credentialsInfo?.last_password_change_at
                  ? new Date(credentialsInfo.last_password_change_at).toLocaleString()
                  : 'Never'}
              </p>
            </div>
          </div>

          {/* Temp Password Warning */}
          {credentialsInfo?.has_temp_password && (
            <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-yellow-600 mt-0.5" />
              <div>
                <p className="font-medium text-yellow-800">Temporary Password Active</p>
                <p className="text-sm text-yellow-700">
                  Vendor has a temporary password that expires on{' '}
                  {new Date(credentialsInfo.temp_password_expires_at).toLocaleString()}.
                  They must change it on their next login.
                </p>
              </div>
            </div>
          )}

          {credentialsInfo?.must_change_password && !credentialsInfo?.has_temp_password && (
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg flex items-start gap-3">
              <Clock className="h-5 w-5 text-blue-600 mt-0.5" />
              <div>
                <p className="font-medium text-blue-800">Password Change Required</p>
                <p className="text-sm text-blue-700">
                  Vendor must change their password on their next login.
                </p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Actions Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Key className="h-5 w-5" />
            Credential Actions
          </CardTitle>
          <CardDescription>
            Generate temporary passwords or manually send credential emails.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Generate Temp Password */}
            <div className="border rounded-lg p-4 space-y-3">
              <h4 className="font-medium flex items-center gap-2">
                <Key className="h-4 w-4" />
                Generate Temporary Password
              </h4>
              <p className="text-sm text-muted-foreground">
                Creates a secure one-time password. The vendor must change it upon first login.
              </p>
              <Button
                onClick={handleGenerateTempPassword}
                disabled={generating}
                className="w-full"
              >
                {generating ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Key className="h-4 w-4 mr-2" />
                    Generate Password
                  </>
                )}
              </Button>
            </div>

            {/* Send Credentials Email */}
            <div className="border rounded-lg p-4 space-y-3">
              <h4 className="font-medium flex items-center gap-2">
                <Mail className="h-4 w-4" />
                Trigger Credentials Email
              </h4>
              <p className="text-sm text-muted-foreground">
                Manually send a credentials email to the vendor with optional custom message.
              </p>
              <Button
                variant="outline"
                onClick={() => setEmailDialog({ open: true })}
                className="w-full"
              >
                <Mail className="h-4 w-4 mr-2" />
                Send Email
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Impersonation Card */}
      <Card className="border-orange-200">
        <CardHeader className="bg-orange-50 rounded-t-lg">
          <CardTitle className="flex items-center gap-2 text-orange-800">
            <LogIn className="h-5 w-5" />
            Login as Vendor
          </CardTitle>
          <CardDescription className="text-orange-700">
            Temporarily access the vendor&apos;s account for support purposes. All actions are logged.
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-4">
          <div className="p-4 bg-orange-50 border border-orange-200 rounded-lg mb-4">
            <h4 className="font-medium text-orange-800 flex items-center gap-2">
              <AlertTriangle className="h-4 w-4" />
              Important Security Notice
            </h4>
            <ul className="text-sm text-orange-700 mt-2 space-y-1">
              <li>• All actions during impersonation are logged in the audit trail</li>
              <li>• Impersonation sessions expire after 1 hour</li>
              <li>• You will have the exact same permissions as this vendor</li>
              <li>• To return to your admin account, log out from the vendor session</li>
            </ul>
          </div>
          <Button
            variant="outline"
            className="w-full border-orange-300 text-orange-700 hover:bg-orange-50"
            onClick={() => setImpersonateDialog({ open: true })}
            disabled={credentialsInfo?.account_status !== 'active'}
          >
            <LogIn className="h-4 w-4 mr-2" />
            Login as {vendorName}
          </Button>
          {credentialsInfo?.account_status !== 'active' && (
            <p className="text-sm text-muted-foreground mt-2 text-center">
              Cannot impersonate inactive vendors
            </p>
          )}
        </CardContent>
      </Card>

      {/* Temp Password Dialog */}
      <Dialog
        open={tempPasswordDialog.open}
        onOpenChange={() => setTempPasswordDialog({ open: false, password: null })}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-green-600" />
              Temporary Password Generated
            </DialogTitle>
            <DialogDescription>
              Share this password securely with the vendor. It will expire in 24 hours.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label className="text-muted-foreground">Temporary Password</Label>
            <div className="flex items-center gap-2 mt-2">
              <div className="flex-1 relative">
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={tempPasswordDialog.password || ''}
                  readOnly
                  className="pr-10 font-mono"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              <Button
                variant="outline"
                size="icon"
                onClick={() => copyToClipboard(tempPasswordDialog.password)}
              >
                <Copy className="h-4 w-4" />
              </Button>
            </div>
            <p className="text-sm text-muted-foreground mt-2">
              The vendor must change this password on their next login.
            </p>
          </div>
          <DialogFooter>
            <Button onClick={() => setTempPasswordDialog({ open: false, password: null })}>
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Send Email Dialog */}
      <Dialog open={emailDialog.open} onOpenChange={(open) => setEmailDialog({ open })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Send Credentials Email</DialogTitle>
            <DialogDescription>
              Send a credentials email to {vendorEmail}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="include_temp_password"
                checked={emailForm.include_temp_password}
                onCheckedChange={(checked) =>
                  setEmailForm({ ...emailForm, include_temp_password: checked })
                }
              />
              <Label htmlFor="include_temp_password">
                Generate and include new temporary password
              </Label>
            </div>
            <div className="space-y-2">
              <Label htmlFor="custom_message">Custom Message (optional)</Label>
              <Textarea
                id="custom_message"
                placeholder="Add a personal message to include in the email..."
                value={emailForm.custom_message}
                onChange={(e) => setEmailForm({ ...emailForm, custom_message: e.target.value })}
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEmailDialog({ open: false })}>
              Cancel
            </Button>
            <Button onClick={handleSendCredentialsEmail} disabled={sendingEmail}>
              {sendingEmail ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Mail className="h-4 w-4 mr-2" />
                  Send Email
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Impersonate Confirmation Dialog */}
      <AlertDialog
        open={impersonateDialog.open}
        onOpenChange={(open) => setImpersonateDialog({ open })}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm Impersonation</AlertDialogTitle>
            <AlertDialogDescription>
              You are about to login as <strong>{vendorName}</strong> ({vendorEmail}).
              This action will be logged in the audit trail.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-4">
            <Label htmlFor="reason">Reason for impersonation (optional)</Label>
            <Input
              id="reason"
              placeholder="e.g., Helping vendor with order issue"
              value={impersonateReason}
              onChange={(e) => setImpersonateReason(e.target.value)}
              className="mt-2"
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleImpersonate} disabled={impersonating}>
              {impersonating ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Starting...
                </>
              ) : (
                <>
                  <LogIn className="h-4 w-4 mr-2" />
                  Login as Vendor
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default VendorCredentialsTab;
